/**
 * Base Cali — product page behaviour (vanilla, no deps).
 *
 *  • Custom variant picker: on option change, find the matching variant and sync
 *    the hidden id input (so Horizon's product-form-component adds the right one),
 *    the price, sale price, availability, the ?variant= URL, and scroll the
 *    gallery to the variant's image.
 *  • Quantity stepper.
 *
 * Prices come pre-formatted from Liquid in the [data-bp-variants] JSON, so no
 * money formatting is needed here.
 */
(function () {
  function initProduct(section) {
    if (section.dataset.bpReady) return;
    section.dataset.bpReady = '1';

    setupVariants(section);
    setupQuantity(section);
    setupAddFeedback(section);
  }

  // Show a spinner the instant Add-to-bag is clicked, then an "Added" state.
  // Reacts to Horizon's cart-update event, with a timeout fallback so it works
  // regardless of the exact event name.
  function setupAddFeedback(section) {
    const pfc = section.querySelector('product-form-component');
    const btn = section.querySelector('[data-bp-add-btn]');
    if (!pfc || !btn) return;
    const form = pfc.querySelector('form');
    if (!form) return;

    let resetTimer;
    let fallbackTimer;

    function reset() {
      btn.classList.remove('is-loading', 'is-added');
    }
    function showAdded() {
      if (!btn.classList.contains('is-loading')) return;
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      window.clearTimeout(resetTimer);
      resetTimer = window.setTimeout(reset, 1700);
    }

    form.addEventListener('submit', function () {
      if (btn.disabled) return;
      btn.classList.remove('is-added');
      btn.classList.add('is-loading');
      window.clearTimeout(fallbackTimer);
      fallbackTimer = window.setTimeout(showAdded, 1200);
    });

    // Horizon dispatches a cart-lines update on the document when the cart changes.
    ['cart:lines:update', 'cart:lines:add', 'cart:update', 'cartLinesUpdate', 'cart:refresh'].forEach(
      function (name) {
        document.addEventListener(name, function () {
          window.clearTimeout(fallbackTimer);
          showAdded();
        });
      }
    );
  }

  function setupVariants(section) {
    const dataEl = section.querySelector('[data-bp-variants]');
    if (!dataEl) return;

    let variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    const optionsRoot = section.querySelector('[data-bp-options]');
    const idInput = section.querySelector('[data-bp-variant-id]');
    const priceEl = section.querySelector('[data-bp-price]');
    const compareEl = section.querySelector('[data-bp-compare]');
    const addBtn = section.querySelector('[data-bp-add-btn]');
    const addLabel = section.querySelector('[data-bp-add-label]');
    const gallery = section.querySelector('[data-bp-gallery]');

    const addText = section.dataset.bpAdd || 'Add to bag';
    const soldText = section.dataset.bpSoldout || 'Sold out';

    // Single-variant products: nothing to wire up.
    if (!optionsRoot) return;

    const fieldsets = Array.from(optionsRoot.querySelectorAll('fieldset'));

    function selectedOptions() {
      return fieldsets.map((fs) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    function matchVariant(sel) {
      return variants.find(
        (v) => v.options.length === sel.length && v.options.every((o, i) => o === sel[i])
      );
    }

    function update() {
      const sel = selectedOptions();

      // Reflect the chosen value in each option's label.
      fieldsets.forEach((fs) => {
        const current = fs.querySelector('[data-bp-option-current]');
        const checked = fs.querySelector('input[type="radio"]:checked');
        if (current && checked) current.textContent = checked.value;
      });

      const variant = matchVariant(sel);

      if (!variant) {
        if (idInput) idInput.value = '';
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
        return;
      }

      if (idInput) {
        idInput.value = variant.id;
        idInput.disabled = false;
      }
      if (priceEl) priceEl.textContent = variant.price;
      if (compareEl) {
        if (variant.compare_gt) {
          compareEl.textContent = variant.compare_at_price;
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      const priceWrap = section.querySelector('.base-product__price');
      if (priceWrap) priceWrap.classList.toggle('is-sale', !!variant.compare_gt);

      if (addBtn) addBtn.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? addText : soldText;

      // Reflect in the URL.
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      } catch (e) {
        /* noop */
      }

      // Scroll the gallery to the variant's image.
      if (variant.media_id && gallery) {
        const media = gallery.querySelector('[data-media-id="' + variant.media_id + '"]');
        if (media) media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }

    optionsRoot.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', update);
    });
  }

  function setupQuantity(section) {
    const wrap = section.querySelector('[data-bp-qty]');
    if (!wrap) return;
    const input = wrap.querySelector('[data-bp-qty-input]');
    const minus = wrap.querySelector('[data-bp-qty-minus]');
    const plus = wrap.querySelector('[data-bp-qty-plus]');
    if (!input) return;

    const clamp = (n) => Math.max(1, isNaN(n) ? 1 : n);
    if (minus) minus.addEventListener('click', () => { input.value = clamp(parseInt(input.value, 10) - 1); });
    if (plus) plus.addEventListener('click', () => { input.value = clamp(parseInt(input.value, 10) + 1); });
    input.addEventListener('change', () => { input.value = clamp(parseInt(input.value, 10)); });
  }

  function boot(root) {
    (root || document).querySelectorAll('[data-base-product]').forEach(initProduct);
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', () => boot());

  // Re-init when the section is reloaded in the theme editor.
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
